import React, {useContext, useState} from "react";
import {
	Box,
	Typography,
	Paper,
	useTheme,
	Skeleton,
	ButtonBase
} from "@mui/material";
import {INotificationSeverity, NotificationContext, useApi} from "@buildwitt/shapeshift-identity";
import {TrainingfeedContext} from "@/contexts/trainingFeedContext";
import {useSSTranslation} from "@/i18n/useSSTranslation";
import PauseStreakDrawer from "@/components/dailyStreak/PauseStreakDrawer";
import PauseStreakDrawerContent from "@/components/dailyStreak/PauseStreakDrawerContent";


const StreakCard = () => {
	const {t} = useSSTranslation();
	const theme = useTheme();
	const {notify} = useContext(NotificationContext)
	const [dailyStreak, refetchStreak] = useApi<any>("/api/user/streakInfo");
	const {trainingfeedState} = useContext(TrainingfeedContext);
	const [openDrawer, setOpenDrawer] = useState(false);

	const raw = dailyStreak.data;
	const streak: number =
		typeof raw === "number" ? raw : (raw && typeof raw.streak === "number" ? raw.streak : 0);
	const isPaused: boolean =
		typeof raw === "boolean" ? raw : (raw && typeof raw.isPaused === "boolean" ? raw.isPaused : false);

	const isPauseStreakEnabled: boolean =
		typeof raw === "boolean" ? raw : (raw && typeof raw.isPauseStreakEnabled === "boolean" ? raw.isPauseStreakEnabled : false);

	const currentDate = new Date();
	currentDate.setHours(0, 0, 0, 0);

	const todayLessons = trainingfeedState?.[currentDate.getTime()];
	const hasCompleteLessonsToday =
		Array.isArray(todayLessons) &&
		todayLessons.length > 0 &&
		todayLessons.every((lesson) => lesson.complete === true);
	const isTodayLessonsEmpty = Array.isArray(todayLessons) && todayLessons.length === 0;

	const streakLabel =
		streak === 1 ? t("streak_day", "day streak") : t("streak_days", "day streak");

	const handleClick = () => {
		setOpenDrawer(true);
	};

	const handleCloseDrawer = (dataFromDrawer?: any) => {
		if (dataFromDrawer?.id) {
			if (dataFromDrawer?.isPaused){
				notify(t("streak_paused_message","Your streak is paused, but your due dates will continue as scheduled."), "Success", INotificationSeverity.success, 5000);
			}else{
				notify(t("streak_unpaused_message","Streak pause removed. "), "Success", INotificationSeverity.success, 5000);
			}
			refetchStreak();
		}
		setOpenDrawer(false);
	};

	if (dailyStreak.isLoading || dailyStreak.data === undefined) {
		return (
			<Paper
				elevation={3}
				sx={{
					display: "flex",
					alignItems: "center",
					color: "white",
					p: "8px",
					borderRadius: "16px",
					backgroundColor: "#282828",
					border: "solid 1px #A1A1A026",
					position: "relative",
					width: {xs: "100%", lg: "51%", xl: "51%"}
				}}
			>
				<Box sx={{width: "93px", height: "89px"}}>
					<Skeleton variant="circular" width={"89px"} height={"89px"} />
				</Box>
				<Box sx={{ml: 2, flex: 1}}>
					<Skeleton variant="text" width="60%" height={32} />
					<Skeleton variant="text" width="90%" height={24} />
					<Skeleton variant="text" width="80%" height={24} />
				</Box>
			</Paper>
		);
	}

	return (
		<>
			<ButtonBase
				onClick={handleClick}
				disabled={!isPauseStreakEnabled}
				sx={{
					width: {xs: "100%", lg: "51%", xl: "51%"},
					borderRadius: "16px",
					display: "block",
					textAlign: "left"
				}}
				TouchRippleProps={{
					style: {color: theme.palette.primary.main}
				}}
			>
				<Paper
					elevation={3}
					sx={{
						display: "flex",
						alignItems: "center",
						color: "white",
						p: "8px",
						borderRadius: "16px",
						backgroundColor: isPaused ? "rgba(40,40,40,0.2)" : "#282828",
						border: "solid 1px",
						borderColor: streak > 99 ? theme.palette.primary.main : "#A1A1A026",
						boxShadow:
						streak > 99
							? "65px 67px 26px rgba(255,158,0,0), 20px 20px 24px rgba(255,158,0,0.01), 8px 8px 20px rgba(255,158,0,0.02), 4px 4px 15px rgba(255,158,0,0.04), 2px 2px 1px rgba(255,158,0,0.07)"
							: "none",
						width: "100%"
					}}
				>
					<Box sx={{opacity: isPaused ? 0.4 : 1}}>
						<PauseStreakDrawerContent streakDays={streak} width={"93px"} height="89px" />
					</Box>

					<Box width={"100%"}>
						<Box flexDirection={"row"} display="flex" alignItems="center" gap={2} mb={1} justifyContent={"space-between"}>
							<Typography variant="h5" sx={{color: theme.palette.text.primary, opacity: isPaused ? 0.4 : 1}}>
								<span style={{color: theme.palette.primary.main}}>
									{streak}
								</span>{" "}
								{streakLabel}
							</Typography>
							{ isPaused && <Typography variant="h6" sx={{color: theme.palette.text.primary, fontWeight: "600", opacity: 0.7}}>
								{t("common_paused", "paused").toUpperCase()}
							</Typography>}
						</Box>

						<Typography variant="body2" sx={{color: "#FFFFFFB2", opacity: isPaused ? 0.2 : 1}}>
							{isTodayLessonsEmpty
								? t("streak_day_off", "No lessons scheduled for today. Enjoy your day off!")
								: hasCompleteLessonsToday
									? t(
										"streak_lesson_completed",
										"Today's lesson completed! Come back tomorrow to continue your streak."
									)
									: t("streak_day_message", "Complete one lesson to keep your streak.")}
						</Typography>
					</Box>
				</Paper>
			</ButtonBase>
			<PauseStreakDrawer
				open={openDrawer}
				onClose={handleCloseDrawer}
				streakDays={streak} isPaused={isPaused}
			/>
		</>
	);
};

export default StreakCard;
